import React from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  FiSearch,
  FiPlay,
  FiVolume2,
  FiPause,
  FiMaximize2,
  FiMinimize2,
  FiX,
  FiCalendar,
  FiClock,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Slider from "@mui/material/Slider";
import Forward5Icon from "@mui/icons-material/Forward5";
import Replay5Icon from "@mui/icons-material/Replay5";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { apiRequest } from "../../api/apiHelper";
import Tooltip from "@mui/material/Tooltip";
import { AssistWalker } from "@mui/icons-material";
import TalkTimeRatio from "../../components/meeting/TalkTimeRatio";
import "./MeetingDetails.css";

// Transform new transcript format to old format for backward compatibility
const transformTranscript = (transcriptData) => {
  if (!transcriptData || transcriptData.length === 0) return [];

  // Check if it's already in the old format (has 'text' field)
  if (transcriptData[0]?.text) {
    return transcriptData;
  }

  // Check if it's in the new format (has 'words' field)
  if (transcriptData[0]?.words) {
    const processedSegments = [];
    const seenTexts = new Set();

    transcriptData.forEach(segment => {
      // Reconstruct text from words array, removing duplicates
      const uniqueWords = [];
      const seenWords = new Set();

      segment.words.forEach(word => {
        const wordText = word.text?.trim();
        if (wordText && !seenWords.has(wordText.toLowerCase())) {
          uniqueWords.push(wordText);
          seenWords.add(wordText.toLowerCase());
        }
      });

      const text = uniqueWords.join(' ').trim();

      // Skip if we've already seen this exact text from this speaker
      const textKey = `${segment.speaker}-${text}`;
      if (text && !seenTexts.has(textKey)) {
        seenTexts.add(textKey);

        // Calculate absolute_start_time from start_time
        const absoluteStartTime = segment.start_time
          ? new Date(Date.now() + segment.start_time * 1000).toISOString()
          : new Date().toISOString();

        processedSegments.push({
          speaker: segment.speaker || 'Unknown',
          text: text,
          absolute_start_time: absoluteStartTime,
          start_time: segment.start_time || 0,
          end_time: segment.end_time || 0,
          bot_id: segment.bot_id,
          id: segment.id
        });
      }
    });

    return processedSegments;
  }

  return [];
};

// Live Meeting Hook for real-time functionality
const useLiveMeeting = (meetingId, isLive) => {
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [liveInsights, setLiveInsights] = useState([]);
  const [liveKeywords, setLiveKeywords] = useState([]);
  const [liveSuggestions, setLiveSuggestions] = useState([]);
  const [liveQaData, setLiveQaData] = useState([]);
  const [liveSummary, setLiveSummary] = useState("");
  const [isLiveLoading, setIsLiveLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const transcriptRef = useRef(null);
  const intervalRef = useRef(null);
  const lastTranscriptLengthRef = useRef(0);
  const [recordingUrl, setRecordingUrl] = useState("");

  // Format transcript for LLM API
  const formatTranscriptForLLM = (transcript) => {
    if (!transcript || transcript.length === 0) return "";

    // Get last 4-5 transcript entries
    const recentTranscripts = transcript.slice(-5);

    return recentTranscripts.map((t) => `${t.speaker}: ${t.text}`).join("\n");
  };

  // Call LLM API for different models
  const callLLMAPI = async (model, transcript) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}` + "/proxy/call-sales-coach",
        {
          model: model,
          prompt: `Transcript: ${transcript}`,
          stream: false,
        }
      );

      if (response && response.data.response) {
        const raw = response.data.response;
        // Try to extract JSON between ```json ... ``` or ``` ... ``` fences
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
        const jsonString = fenceMatch
          ? fenceMatch[1]
          : raw
            .replace(/```/g, "")
            .replace(/^\s*json\s*/i, "")
            .trim();

        try {
          const parsedData = JSON.parse(jsonString);
          return parsedData;
        } catch (e) {
          console.error("Failed to parse LLM JSON:", e);
          return null;
        }
      }
    } catch (error) {
      console.error(`Error calling LLM API for ${model}:`, error);
    }
    return null;
  };

  // Fetch live transcript
  const fetchLiveTranscript = async () => {
    try {
      setIsLiveLoading(false);
      const response = await apiRequest(
        "get",
        `/trans/transcript/${meetingId}?live=true`
      );
      const newTranscript = response.transcript || [];
      console.log("Live transcript fetched:", newTranscript);

      // Set recording URL if available
      if (response.recordingUrl) {
        setRecordingUrl(response.recordingUrl);
      }

      // Check if transcript length has changed
      const transcriptLengthChanged =
        newTranscript.length !== lastTranscriptLengthRef.current;

      // Only update if transcript has changed
      if (JSON.stringify(newTranscript) !== JSON.stringify(liveTranscript)) {
        console.log("Transcript updated, setting new transcript");
        setLiveTranscript(newTranscript);
        lastTranscriptLengthRef.current = newTranscript.length;

        // Show typing indicator if transcript length increased
        if (
          transcriptLengthChanged &&
          newTranscript.length > liveTranscript.length
        ) {
          setIsTyping(true);
          // Hide typing indicator after 2 seconds
          setTimeout(() => setIsTyping(false), 2000);
        }

        // Scroll to bottom of transcript container
        if (transcriptRef.current) {
          transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
        }

        // Call LLM APIs with recent transcript only if transcript length changed
        if (transcriptLengthChanged) {
          const formattedTranscript = formatTranscriptForLLM(newTranscript);
          if (formattedTranscript) {
            // Call insights API only if we don't have insights yet
            if (liveInsights.length === 0) {
              const insightsData = await callLLMAPI(
                "insights",
                formattedTranscript
              );
              if (insightsData?.insights) {
                setLiveInsights(insightsData.insights);
              }
            }

            // Call suggestions API only if we don't have suggestions yet
            if (liveSuggestions.length === 0) {
              const suggestionsData = await callLLMAPI(
                "suggestions",
                formattedTranscript
              );
              if (suggestionsData?.suggestions) {
                setLiveSuggestions(suggestionsData.suggestions);
              }
              if (suggestionsData?.keywords) {
                setLiveKeywords(suggestionsData.keywords);
              }
            }

            // Call Q&A API only if we don't have Q&A yet
            if (liveQaData.length === 0) {
              const qaData = await callLLMAPI("qna", formattedTranscript);
              if (qaData?.questions_and_answers) {
                setLiveQaData(qaData.questions_and_answers);
              }
            }

            // Call meeting summary API only if we don't have summary yet
            if (!liveSummary) {
              const summaryData = await callLLMAPI(
                "meeting-summary",
                formattedTranscript
              );
              if (summaryData?.meeting_summary) {
                setLiveSummary(summaryData.meeting_summary);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching live transcript:", error);
    } finally {
      setIsLiveLoading(false);
    }
  };

  // Start live updates
  useEffect(() => {
    if (isLive && meetingId) {
      // Initial fetch
      fetchLiveTranscript();

      // Set up interval for every 10 seconds
      intervalRef.current = setInterval(fetchLiveTranscript, 2000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isLive, meetingId]);

  return {
    liveTranscript,
    liveInsights,
    liveKeywords,
    liveSuggestions,
    liveQaData,
    liveSummary,
    isLiveLoading,
    isTyping,
    transcriptRef,
    fetchLiveTranscript,
    recordingUrl,
    setRecordingUrl,
  };
};

// import Box from "@mui/material/Box";

// Generate random colors for speakers
const generateSpeakerColor = (speakerName = "Bot") => {
  if (speakerName == null) {
    speakerName = "bot";
  }
  // console.log("speakerName: ", speakerName);
  const colors = [
    "#1976d2",
    "#43cea2",
    "#f44336",
    "#ff9800",
    "#9c27b0",
    "#00bcd4",
    "#4caf50",
    "#ff5722",
    "#795548",
    "#607d8b",
  ];
  // Use speaker name to generate consistent color
  const hash = speakerName.split("").reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
  return colors[Math.abs(hash) % colors.length];
};

// Format time from absolute_start_time
const formatTranscriptTime = (absoluteTime) => {
  if (!absoluteTime) return "";
  try {
    const date = new Date(absoluteTime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch (error) {
    return "";
  }
};

// Capitalize first letter of each word
const capitalizeWords = (str = "Bot") => {
  return str
    ?.split(" ")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const speakers = [
  {
    name: "Piryanshu Bisht",
    role: "Software developer",
    group: "BOXOFIC",
    color: "#009688",
    percent: 67,
    minutes: 12,
  },
  {
    name: "Sagar",
    group: "OTHERS",
    color: "#f44336",
    percent: 33,
    minutes: 6,
  },
];

const topicSegments = [
  { topic: "Project Status Update", color: "#1976d2", percent: 30 },
  { topic: "Design Concept Presentation", color: "#43cea2", percent: 40 },
  { topic: "Discussion", color: "#f44336", percent: 20 },
  { topic: "Action Items", color: "#ff9800", percent: 10 },
];

// const qaData = [
//   {
//     question: "Will the changes impact the mobile app as well?",
//     answer:
//       "Yes, Sagar. We'll ensure a consistent experience across all platforms.",
//     speaker: "Sagar",
//     time: "08:54",
//   },
//   {
//     question: "Any concerns about backend integration?",
//     answer:
//       "Good question, Sagar. I'll make sure to sync up with the development team to ensure a smooth integration process.",
//     speaker: "Sagar",
//     time: "08:29",
//   },
//   {
//     question: "Can you take note of the action items?",
//     answer:
//       "Absolutely. I'll coordinate with the development team for integration and circulate an updated design document by the end of the week.",
//     speaker: "Kour",
//     time: "08:38",
//   },
// ];

function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

// Topic colors for dynamic topics
const topicColors = ["#1976d2", "#43cea2", "#f44336", "#ff9800", "#9c27b0", "#00bcd4", "#4caf50"];

// Convert timestamp string to seconds
const timestampToSeconds = (timestamp) => {
  if (!timestamp) return 0;
  const parts = timestamp.split(':');
  if (parts.length === 3) {
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
  } else if (parts.length === 2) {
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  return 0;
};

// Get current topic based on time
const getCurrentTopic = (currentTime, topics) => {
  if (!topics || topics.length === 0) return null;
  return topics.find(topic =>
    currentTime >= topic.startTime && currentTime < topic.endTime
  ) || topics[0];
};

// Professional WaveSurfer Audio Visualizer Component
const WaveformVisualizer = ({ audioUrl, isPlaying, onPlayPause }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);

  useEffect(() => {
    if (!waveformRef.current || !audioUrl) return;

    // Initialize WaveSurfer
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#d4d4d8', // Light gray for unplayed portion
      progressColor: '#7c3aed', // Purple gradient for played portion
      cursorColor: '#7c3aed',
      barWidth: 3,
      barRadius: 3,
      barGap: 2,
      responsive: true,
      height: 120,
      normalize: true,
      backend: 'WebAudio',
      backgroundColor: 'transparent',
    });

    // Load audio with error handling
    if (audioUrl) {
      wavesurfer.load(audioUrl).catch(err => {
        // Ignore AbortError and other load interruptions
        if (err.name !== 'AbortError') {
          console.warn('WaveSurfer load error:', err);
        }
      });
    }

    // Store reference
    wavesurferRef.current = wavesurfer;

    // Sync play/pause state
    wavesurfer.on('ready', () => {
      if (isPlaying) {
        wavesurfer.play();
      }
    });

    wavesurfer.on('finish', () => {
      if (onPlayPause) onPlayPause(false);
    });

    // Cleanup
    return () => {
      try {
        if (wavesurfer) {
          wavesurfer.destroy();
        }
      } catch (e) {
        // Ignore AbortError as it's expected during unmount if loading is in progress
        if (e.name !== 'AbortError') {
          console.error('Error destroying wavesurfer instance:', e);
        }
      }
    };
  }, [audioUrl]);

  // Sync play/pause with external control
  useEffect(() => {
    if (wavesurferRef.current) {
      if (isPlaying) {
        wavesurferRef.current.play();
      } else {
        wavesurferRef.current.pause();
      }
    }
  }, [isPlaying]);

  return (
    <Box
      ref={ waveformRef }
      sx={ {
        width: '90%',
        height: '120px',
        margin: '0 auto',
        cursor: 'pointer',
        '& wave': {
          overflow: 'visible !important'
        }
      } }
      onClick={ () => {
        if (onPlayPause) onPlayPause(!isPlaying);
      } }
    />
  );
};

// Word Highlighter Component
function WordHighlighter({ words, segmentStartTime, isActive, searchQuery = "", currentTime, timingOffset = 0 }) {
  const [currentVideoTime, setCurrentVideoTime] = useState(0);

  useEffect(() => {
    let rafId;
    const tick = () => {
      const mediaElement = document.querySelector('video') || document.querySelector('audio');
      if (mediaElement && !mediaElement.paused) {
        setCurrentVideoTime(mediaElement.currentTime);
      } else if (typeof currentTime === 'number') {
        setCurrentVideoTime(currentTime);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [currentTime]);

  if (!words || words.length === 0) return null;

  return (
    <span>
      { words.map((word, wordIndex) => {
        const wordText = word.text || word;
        let wordStartTime = typeof word.start_time === 'number' ? word.start_time : null;
        let wordEndTime = typeof word.end_time === 'number' ? word.end_time : null;
        
        // Apply timing offset if provided
        if (wordStartTime !== null && timingOffset !== 0) {
          wordStartTime += timingOffset;
        }
        if (wordEndTime !== null && timingOffset !== 0) {
          wordEndTime += timingOffset;
        }
        
        // Use the more accurate time source
        const effectiveTime = typeof currentTime === 'number' ? currentTime : currentVideoTime;

        // Debug logging for first few words to check timing
        if (wordIndex < 3) {
          // console.log(`Word "${wordText}": start=${wordStartTime}, end=${wordEndTime}, currentTime=${effectiveTime}, offset=${timingOffset}`);
        }

        // Enhanced word highlighting logic with better precision
        const isWordActive = wordStartTime !== null && wordEndTime !== null &&
          effectiveTime >= wordStartTime &&
          effectiveTime <= wordEndTime;
          
        // Add a small buffer for better visual experience
        const isWordNearActive = wordStartTime !== null && wordEndTime !== null &&
          effectiveTime >= (wordStartTime - 0.2) &&
          effectiveTime <= (wordEndTime + 0.2);
          
        const isSearchMatch = searchQuery && wordText.toLowerCase().includes(searchQuery.toLowerCase());

        return (
          <span
            key={ wordIndex }
            style={ {
              background: isWordActive 
                ? 'rgba(59,130,246,0.85)'
                : isWordNearActive 
                  ? 'rgba(59, 130, 246, 0.15)' 
                  : isSearchMatch 
                    ? '#ffeb3b' 
                    : 'transparent',
              color: isWordActive 
                ? 'white' 
                : isActive 
                  ? 'var(--primary-color)' 
                  : 'inherit',
              fontWeight: isWordActive ? 600 : isActive ? 500 : 400,
              fontSize: 'inherit',
              padding: isWordActive ? '1px 4px' : isWordNearActive || isSearchMatch ? '1px 3px' : '0',
              borderRadius: isWordActive ? '4px' : isWordNearActive || isSearchMatch ? '3px' : '0',
              margin: '0 1px',
              transition: 'background 0.1s ease, color 0.1s ease',
              boxShadow: 'none',
              transform: 'none',
              display: 'inline',
              textShadow: 'none',
            } }
          >
            { wordText }{ ' ' }
          </span>
        );
      }) }
    </span>
  );
}

// Advanced Video Player Component with Avoma-like features
function VideoPlayer({ videoUrl, audioUrl, transcript = [], onTranscriptSync, topics = [] }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0); // Track exact time for word highlight
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(topics[0] || null);
  const [activeTranscriptIndex, setActiveTranscriptIndex] = useState(-1);
  const activeTranscriptIndexRef = useRef(-1); // Ref to avoid listener churn

  // Format time display
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };



  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const time = video.currentTime;
      setCurrentTime(time);

      // Update current topic
      if (topics && topics.length > 0) {
        const topic = getCurrentTopic(time, topics);
        setCurrentTopic(topic);
      }

      // Find active transcript segment with improved precision
      const activeIndex = transcript.findIndex((t, index) => {
        // If start_time is 0 and next one is also 0, assume timestamps are invalid
        // and do not calculate active index unless we have meaningful data
        if (!t || typeof t.start_time !== 'number') return false;

        const nextT = transcript[index + 1];
        // If current and next have same start time (e.g. both 0), we can't distinguish
        if (nextT && t.start_time === 0 && nextT.start_time === 0) return false;

        return time >= t.start_time && (!nextT || time < nextT.start_time);
      });

      // Always sync time for word highlighting with high precision
      if (onTranscriptSync) {
        onTranscriptSync(activeIndex, time);
      }

      if (activeIndex !== -1 && activeIndex !== activeTranscriptIndexRef.current) {
        activeTranscriptIndexRef.current = activeIndex;
        setActiveTranscriptIndex(activeIndex);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      if (onTranscriptSync) {
        onTranscriptSync(-1, 0, video.duration);
      }
    };

    const handlePlay = () => {
      console.log("Media play event fired");
      setIsPlaying(true);
      // Pause all audio when video starts playing
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        if (!audio.paused && audio !== video) {
          audio.pause();
        }
      });
    };
    const handlePause = () => {
      console.log("Media pause event fired");
      setIsPlaying(false);
    };

    // Use higher frequency time updates for better word highlighting
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // Add additional high-frequency time tracking for smooth word highlighting
    let animationFrame;
    const trackTime = () => {
      if (!video.paused) {
        const time = video.currentTime;
        setCurrentTime(time);
        if (onTranscriptSync) {
          onTranscriptSync(activeTranscriptIndexRef.current, time);
        }
      }
      animationFrame = requestAnimationFrame(trackTime);
    };
    
    if (!video.paused) {
      trackTime();
    }

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [transcript, onTranscriptSync, topics, videoUrl, audioUrl]);

  // Control functions
  // Control functions
  const togglePlay = async () => {
    if (videoRef.current) {
      try {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          // Pause all other audio elements when video plays
          const audioElements = document.querySelectorAll('audio');
          audioElements.forEach(audio => {
            if (audio !== videoRef.current && !audio.paused) {
              audio.pause();
              console.log('Paused other audio element');
            }
          });

          await videoRef.current.play();
        }
      } catch (err) {
        console.error("Playback error:", err);
        // Ensure state is synced if play failed
        setIsPlaying(false);
      }
    }
  };

  const skipTime = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];

    if (videoRef.current) {
      videoRef.current.playbackRate = nextRate;
      setPlaybackRate(nextRate);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const time = pos * duration;

    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleTopicClick = (topic) => {
    if (videoRef.current && topic.startTime !== undefined) {
      videoRef.current.currentTime = topic.startTime;
      if (videoRef.current.paused) {
        videoRef.current.play();
      }
    }
  };

  if (!videoUrl && !audioUrl) {
    return (
      <Box className="video-not-available">
        <Typography className="video-not-available-text">
          📹 Video/Audio not available
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="video-container">
      {/* Video Element */ }
      <Box className="video-wrapper">
        { videoUrl ? (
          <video
            ref={ videoRef }
            className="video-element"
            src={ videoUrl }
          />
        ) : (
          <>
            <audio
              ref={ videoRef }
              src={ audioUrl }
              className="video-element"
              style={ { display: 'none' } }
            />
            {/* Audio Visualizer */ }
            <Box
              className={ `audio-visualizer ${isPlaying ? 'playing' : ''}` }
              sx={ { cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' } }
            >
              <WaveformVisualizer
                audioUrl={ audioUrl }
                isPlaying={ isPlaying }
                onPlayPause={ (playing) => {
                  if (playing && videoRef.current && videoRef.current.paused) {
                    videoRef.current.play();
                  } else if (!playing && videoRef.current && !videoRef.current.paused) {
                    videoRef.current.pause();
                  }
                } }
              />
            </Box>
          </>
        ) }

        {/* Center Play/Pause Button */ }
        <Box
          className={ `center-play-button ${isPlaying ? 'playing' : ''}` }
          onClick={ togglePlay }
        >
          { isPlaying ? <FiPause size={ 48 } color="white" /> : <FiPlay size={ 48 } color="white" /> }
        </Box>

        {/* Custom Controls Overlay */ }
        <Box className="video-controls-overlay">
          {/* Progress Bar with Topic Colors */ }
          <Box sx={ { mb: 2 } }>
            <Box
              className="progress-bar-container"
              onClick={ handleSeek }
            >
              {/* Topic segments */ }
              { topics && topics.length > 0 && topics.map((topic, index) => {
                const startPercent = (topic.startTime / duration) * 100;
                const widthPercent = ((topic.endTime - topic.startTime) / duration) * 100;
                return (
                  <Box
                    key={ index }
                    sx={ {
                      position: "absolute",
                      left: `${startPercent}%`,
                      width: `${widthPercent}%`,
                      height: "100%",
                      background: topic.color,
                      opacity: 0.7
                    } }
                  />
                );
              }) }

              {/* Current progress */ }
              <Box
                className="progress-bar-current"
                sx={ {
                  width: `${(currentTime / duration) * 100}%`
                } }
              />

              {/* Progress handle */ }
              <Box
                className="progress-handle"
                sx={ {
                  left: `${(currentTime / duration) * 100}%`
                } }
              />
            </Box>
          </Box>

          {/* Controls */ }
          <Box className="video-controls">
            {/* Play/Pause */ }
            <IconButton onClick={ togglePlay } className="control-button" sx={ { padding: 1.5 } }>
              { isPlaying ? <FiPause size={ 32 } /> : <FiPlay size={ 32 } /> }
            </IconButton>

            {/* Skip buttons */ }
            <IconButton onClick={ () => skipTime(-10) } className="control-button" sx={ { padding: 1.5 } }>
              <Replay5Icon sx={ { fontSize: 32 } } />
            </IconButton>
            <IconButton onClick={ () => skipTime(10) } className="control-button" sx={ { padding: 1.5 } }>
              <Forward5Icon sx={ { fontSize: 32 } } />
            </IconButton>

            {/* Volume */ }
            <IconButton onClick={ toggleMute } className="control-button" sx={ { padding: 1.5 } }>
              <FiVolume2 size={ 28 } />
            </IconButton>

            {/* Time display */ }
            <Typography className="time-display">
              { formatTime(currentTime) } / { formatTime(duration) }
            </Typography>

            {/* Current topic */ }
            { currentTopic && (
              <Box
                className="current-topic"
                sx={ {
                  background: currentTopic.color
                } }
              >
                { currentTopic.name }
              </Box>
            ) }

            <Box sx={ { flex: 1 } } />

            {/* Playback speed */ }
            <Button
              onClick={ changePlaybackRate }
              className="playback-speed-button"
            >
              { playbackRate }x
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Topic Navigation */ }
      { topics && topics.length > 0 && (
        <Box className="topic-navigation">
          <Typography className="topic-navigation-title">
            Topics
          </Typography>
          <Box className="topic-chips-container">
            { topics.map((topic, index) => (
              <Chip
                key={ index }
                label={ `${topic.name} (${formatTime(topic.startTime)})` }
                onClick={ () => handleTopicClick(topic) }
                className="topic-chip"
                sx={ {
                  background: topic.color,
                  opacity: currentTopic?.name === topic.name ? 1 : 0.7,
                  "&:hover": { opacity: 1 }
                } }
              />
            )) }
          </Box>
        </Box>
      ) }
    </Box>
  );
}

// Format duration for display
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function MeetingDetails() {
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [tab, setTab] = useState(0);
  const [transcriptTab, setTranscriptTab] = useState(0);
  const [transcriptLoading, setTranscriptLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [meetingPopupOpen, setMeetingPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 20, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [activeTranscriptIndex, setActiveTranscriptIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [transcriptLanguage, setTranscriptLanguage] = useState("english"); // New state for language toggle
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // console.log("params: ", params);
  const { id } = useParams();
  const isLiveMeeting = params.get("live") === "true";
  const [transcript, setTranscript] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [meetingDetails, setMeetingDetails] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [meetingEndTime, setMeetingEndTime] = useState("");
  const [meetingName, setMeetingName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [insights, setInsights] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [qaData, setQaData] = useState([]);
  const [meetingSummary, setMeetingSummary] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [meetingDuration, setMeetingDuration] = useState(0);
  const [totalSpeakers, setTotalSpeakers] = useState(0);
  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);

  const [speakersfr, setSpeakers] = useState({});

  // Live meeting hook
  const {
    liveTranscript,
    liveInsights,
    liveKeywords,
    liveSuggestions,
    liveQaData,
    liveSummary,
    isLiveLoading,
    isTyping,
    transcriptRef,
    fetchLiveTranscript,
    recordingUrl,
    setRecordingUrl,
  } = useLiveMeeting(id, isLiveMeeting);

  // Use live data if it exists, otherwise fall back to stored data
  // Priority: liveData (if exists) > storedData
  const currentTranscript =
    liveTranscript && liveTranscript.length > 0 ? liveTranscript : transcript;
  const currentInsights =
    liveInsights && liveInsights.length > 0 ? liveInsights : insights;
  const currentKeywords =
    liveKeywords && liveKeywords.length > 0 ? liveKeywords : keywords;
  const currentSuggestions =
    liveSuggestions && liveSuggestions.length > 0
      ? liveSuggestions
      : suggestions;
  const currentQaData =
    liveQaData && liveQaData.length > 0 ? liveQaData : qaData;
  const currentSummary =
    liveSummary && liveSummary.trim() !== "" ? liveSummary : meetingSummary;
  const currentActionItems = actionItems; // For now, actionItems don't have live equivalent

  // Calculate timing offset for word highlighting
  // If the first transcript segment starts at a different time than the first word,
  // we need to adjust the word timestamps
  const calculateTimingOffset = () => {
    if (!currentTranscript || currentTranscript.length === 0) return 0;
    
    const firstSegment = currentTranscript[0];
    if (!firstSegment || !firstSegment.words || firstSegment.words.length === 0) return 0;
    
    const segmentStartTime = firstSegment.start_time || 0;
    const firstWordStartTime = firstSegment.words[0].start_time || 0;
    
    // If there's a significant difference, use it as offset
    const offset = segmentStartTime - firstWordStartTime;
    // console.log("Timing offset calculated:", { segmentStartTime, firstWordStartTime, offset });
    
    return Math.abs(offset) > 1 ? offset : 0; // Only apply if offset is significant
  };

  const timingOffset = calculateTimingOffset();

  // Debug logging
  // console.log("Data check:", {
  //   transcript: { live: liveTranscript?.length, stored: transcript?.length, current: currentTranscript?.length },
  //   insights: { live: liveInsights?.length, stored: insights?.length, current: currentInsights?.length },
  //   keywords: { live: liveKeywords?.length, stored: keywords?.length, current: currentKeywords?.length },
  //   suggestions: { live: liveSuggestions?.length, stored: suggestions?.length, current: currentSuggestions?.length },
  //   actionItems: { stored: actionItems?.length, current: currentActionItems?.length },
  //   summary: { live: liveSummary?.length, stored: meetingSummary?.length, current: currentSummary?.length },
  //   recordingUrl,
  //   isLiveMeeting
  // });

  // console.log("Current transcript data:", currentTranscript);
  // console.log("Keywords data:", keywords);
  // console.log("Current keywords:", currentKeywords);

  // Debug logging for live meeting
  if (isLiveMeeting) {
    console.log("Live meeting data:", {
      liveTranscript,
      currentTranscript,
      isLiveLoading,
      isTyping,
      liveInsights,
      liveKeywords,
      liveSuggestions,
      liveQaData,
      liveSummary,
    });
  }

  // Handle language toggle
  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage !== null) {
      setTranscriptLanguage(newLanguage);
      // Here you can add API call to fetch transcript in different language
      // For now, we'll just update the state
      console.log("Language changed to:", newLanguage);
      
      // TODO: Add translation API integration here
      // Example:
      // if (newLanguage === 'hindi') {
      //   translateTranscript(currentTranscript, 'hi');
      // } else {
      //   // Reset to original English transcript
      //   setTranscript(originalTranscript);
      // }
    }
  };

  // Function to translate transcript (placeholder for future implementation)
  const translateTranscript = async (transcript, targetLanguage) => {
    // This is a placeholder function for translation
    // You can integrate with Google Translate API, Azure Translator, or any other service
    try {
      // Example API call structure:
      // const translatedTranscript = await apiRequest('post', '/translate', {
      //   text: transcript.map(t => t.text),
      //   targetLanguage: targetLanguage,
      //   sourceLanguage: 'en'
      // });
      // setTranscript(translatedTranscript);
      
      console.log(`Translation to ${targetLanguage} would happen here`);
    } catch (error) {
      console.error('Translation error:', error);
    }
  };

  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = currentTranscript.filter((item) => {
      const speakerMatch = item.speaker?.toLowerCase().includes(query.toLowerCase());
      const textMatch = item.text?.toLowerCase().includes(query.toLowerCase());
      return speakerMatch || textMatch;
    }).map((item) => ({
      ...item,
      originalIndex: currentTranscript.indexOf(item)
    }));

    setSearchResults(results);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleTranscriptTabChange = (event, newValue) => {
    setTranscriptTab(newValue);
  };
  // let meetinid = "bvs-xqec-bgw";

  // Popup drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPopupPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Handlers for add to calendar/task (call backend APIs)
  const handleAddToCalendar = async (item) => {
    if (!item) return;
    try {
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const email = userData.email || "";
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const startTime = meetingStartTime
        ? new Date(meetingStartTime).toISOString()
        : new Date().toISOString();
      const endTime = meetingEndTime
        ? new Date(meetingEndTime).toISOString()
        : new Date(new Date(startTime).getTime() + 60 * 60 * 1000).toISOString();

      const payload = {
        email,
        summary: item.task || meetingTitle || "Action Item",
        description: item.task || meetingSummary || "",
        startTime,
        endTime,
        timeZone: userTimezone,
        attendees: item.attendees || [],
        conversationType: "meet",
        dealId: item.dealId || null,
      };

      await apiRequest("post", "/calendar/create-event-v1", payload);
      alert("Calendar event created successfully.");
    } catch (err) {
      console.error("Failed to create calendar event:", err);
      alert("Failed to create calendar event. Please try again.");
    }
  };

  const handleAddToTask = async (item) => {
    if (!item) return;
    try {
      // Intentionally not reading userData here; tasks should be created unassigned

      // Try to parse a due date if item.deadline looks like a date
      let dueDate = null;
      if (item.deadline) {
        const parsed = Date.parse(item.deadline);
        if (!isNaN(parsed)) dueDate = new Date(parsed).toISOString();
      }
      if (!dueDate && meetingEndTime) {
        dueDate = new Date(meetingEndTime).toISOString();
      }

      // We intentionally leave `assignedTo` null to create unassigned tasks

      const payload = {
        taskTitle: item.task || meetingTitle || "Action Item",
        description: item.task || meetingSummary || "",
        assignedTo: null,
        dueDate: dueDate,
        source: "meeting",
        referenceId: id || null,
        tab: "Daily",
      };

      await apiRequest("post", "/tasks/create", payload);
      alert("Task created successfully.");
    } catch (err) {
      console.error("Failed to create task:", err);
      alert("Failed to create task. Please try again.");
    }
  };

  const getTranscript = async () => {
    try {
      setTranscriptLoading(true);
      // console.log("Fetching transcript for ID:", id);

      let response = await apiRequest(
        "get",
        `/trans/transcript_v1/${id}`
      );
      // console.log("NEW API RESPONSE:", response);

      // Handle both postMeetingTranscript and transcript fields
      let rawTranscript = response?.transcript || [];
      // console.log("Raw transcript data:", rawTranscript);

      // If no transcript data, show message but still set audio URL if available
      if (!rawTranscript || rawTranscript.length === 0) {
        console.log("No transcript data found in response");
        setTranscript([]);
      } else {
        // Transform transcript to consistent format
        const transformedTranscript = transformTranscript(rawTranscript);
        // console.log("Transformed transcript for UI:", transformedTranscript);
        setTranscript(transformedTranscript);
      }

      // Set recording URLs from response
      const audioUrl = response?.audioUrl || response?.audioRecordedUrl || null;
      const videoUrl = response?.videoUrl || response?.videoRecordedUrl || null;

      console.log("Setting URLs:", { audioUrl, videoUrl });
      setRecordingUrl(audioUrl);
      setVideoUrl(videoUrl);

      // Set meeting metadata
      setMeetingName(response?.meetingName || "");
      setMeetingStartTime(response?.startTime || "");
      setCreatedBy(response?.createBy || "");

      // Calculate total speakers
      if (rawTranscript && rawTranscript.length > 0) {
        const processedTranscript = transformTranscript(rawTranscript);
        const uniqueSpeakers = new Set(processedTranscript.map(t => t.speaker).filter(Boolean));
        setTotalSpeakers(uniqueSpeakers.size);
      }

    } catch (error) {
      console.error("Error fetching transcript:", error);
      setTranscript([]);
      setRecordingUrl(null);
    } finally {
      setTranscriptLoading(false);
    }
  };

  const calculateTalktimeRatio = async () => {
    try {
      const response = await apiRequest(
        "get",
        `/trans/calculate-speaking-time/${id}`
      );
      console.log("response: ", response);
      setSpeakers(response);
    } catch (err) {
      console.log("err", err);
    }
  };

  // Helper to normalize array values (handles both old and new formats)
  const normalizeArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(item => {
        if (typeof item === 'string') return item.trim();
        if (typeof item === 'object' && item !== null) {
          // Try multiple property names
          if (item.text) return String(item.text).trim();
          if (item.value) return String(item.value).trim();
          if (item.suggestion) return String(item.suggestion).trim();
          if (item.title) return String(item.title).trim();
          if (item.name) return String(item.name).trim();
          if (item.content) return String(item.content).trim();
          // Don't convert object to string - return null instead
          console.warn("⚠️ Could not normalize object:", item);
          return null;
        }
        return String(item).trim();
      }).filter(Boolean);
    }
    if (typeof value === 'string') return [value.trim()];
    if (typeof value === 'object' && value !== null) {
      if (value.text) return [String(value.text).trim()];
      if (value.value) return [String(value.value).trim()];
      console.warn("⚠️ Could not normalize single object:", value);
      return [];
    }
    return [];
  };

  // Helper to normalize object values
  const normalizeValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object' && value !== null) {
      if (value.value) return String(value.value).trim();
      if (value.text) return String(value.text).trim();
      if (value.content) return String(value.content).trim();
      console.warn("⚠️ Could not normalize value object:", value);
      return null;
    }
    return String(value).trim();
  };

  // Function to parse phase responses (handles both old and new formats)
  const parsePhaseResponses = (phaseResponses) => {
    let allInsights = [];
    let allActionItems = [];
    let allKeywords = [];
    let allSuggestions = [];
    let allQaData = [];
    let meetingSummary = "";

    // console.log("=== Starting parsePhaseResponses ===");
    // console.log("Phase responses keys:", Object.keys(phaseResponses));

    // Parse each phase response
    Object.values(phaseResponses).forEach((phaseResponse, index) => {
      if (!phaseResponse) return;

      // console.log(`Processing phase ${index + 1}:`, phaseResponse.substring(0, 200) + "...");

      try {
        // Extract JSON from phase response
        const fenceMatch = phaseResponse.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
        let jsonString = fenceMatch
          ? fenceMatch[1]
          : phaseResponse
            .replace(/```/g, "")
            .replace(/^\s*json\s*/i, "")
            .trim();

        // console.log(`Phase ${index + 1} JSON string:`, jsonString.substring(0, 300) + "...");

        // Fix common JSON issues
        let cleanedJsonString = jsonString
          .replace(/,\s*}/g, '}')  // Remove trailing commas before }
          .replace(/,\s*]/g, ']')  // Remove trailing commas before ]
          .replace(/:\s*i(\d+)/g, ': $1')  // Fix i20 -> 20
          .replace(/:\s*,/g, ': null,');  // Fix empty values

        const parsedPhase = JSON.parse(cleanedJsonString);
        console.log(`Phase ${index + 1} parsed data:`, parsedPhase);

        // Phase 1: Customer Profiling (handles both old and new formats)
        if (parsedPhase.customer_profiling) {
          const painPoints = normalizeArray(parsedPhase.customer_profiling.pain_points);
          const goals = normalizeArray(parsedPhase.customer_profiling.goals);

          if (painPoints.length > 0) {
            allInsights.push({
              header: "Pain Points",
              points: painPoints
            });
          }
          if (goals.length > 0) {
            allInsights.push({
              header: "Customer Goals",
              points: goals
            });
          }
        }

        // Decision Drivers (handles both old and new formats)
        const decisionDrivers = normalizeArray(parsedPhase.decision_drivers);
        if (decisionDrivers.length > 0) {
          allInsights.push({
            header: "Decision Drivers",
            points: decisionDrivers
          });
        }

        // Phase 2: Deal Health & Risk Analysis (handles both old and new formats)
        if (parsedPhase.deal_health) {
          const positiveSignals = normalizeArray(parsedPhase.deal_health.positive_signals);
          const negativeSignals = normalizeArray(parsedPhase.deal_health.negative_signals);

          if (positiveSignals.length > 0) {
            allInsights.push({
              header: "Positive Signals",
              points: positiveSignals
            });
          }
          if (negativeSignals.length > 0) {
            allInsights.push({
              header: "Negative Signals",
              points: negativeSignals
            });
          }
        }

        const riskFactors = normalizeArray(parsedPhase.risk_analysis?.risk_factors);
        if (riskFactors.length > 0) {
          allInsights.push({
            header: "Risk Factors",
            points: riskFactors
          });
        }

        // Phase 3: Strategic Insights (handles both old and new formats)
        if (parsedPhase.strategic_insights) {
          const opportunityRisks = normalizeArray(parsedPhase.strategic_insights.opportunity_risks);
          const salesOpportunities = normalizeArray(parsedPhase.strategic_insights.sales_opportunities);

          if (opportunityRisks.length > 0) {
            allInsights.push({
              header: "Opportunity Risks",
              points: opportunityRisks
            });
          }
          if (salesOpportunities.length > 0) {
            allInsights.push({
              header: "Sales Opportunities",
              points: salesOpportunities
            });
          }
        }

        // Extract action items (handles both old and new formats)
        // console.log("Checking action items in parsedPhase:", parsedPhase.action_items);
        const salesActionItems = normalizeArray(parsedPhase.action_items?.sales_action_items);
        const customerActionItems = normalizeArray(parsedPhase.action_items?.customer_action_items);

        // Get deadline - handles both old format (object) and new format (array)
        let deadline = "";
        if (parsedPhase.action_items?.due_dates) {
          if (Array.isArray(parsedPhase.action_items.due_dates)) {
            deadline = parsedPhase.action_items.due_dates[0]?.text || "";
          } else {
            deadline = parsedPhase.action_items.due_dates.onboarding_session ||
              parsedPhase.action_items.due_dates.calendar_invite || "";
          }
        }

        if (salesActionItems.length > 0) {
          console.log("Found sales_action_items:", salesActionItems);
          allActionItems.push(...salesActionItems.map(item => ({
            task: item,
            assigned_to: "Sales Team",
            deadline: deadline
          })));
        }
        if (customerActionItems.length > 0) {
          console.log("Found customer_action_items:", customerActionItems);
          allActionItems.push(...customerActionItems.map(item => ({
            task: item,
            assigned_to: "Customer",
            deadline: deadline
          })));
        }

        // Next steps as action items (handles both old and new formats)
        const immediateActions = normalizeArray(parsedPhase.next_steps?.immediate_actions);
        const longTermActions = normalizeArray(parsedPhase.next_steps?.long_term_actions);

        if (immediateActions.length > 0) {
          allActionItems.push(...immediateActions.map(item => ({
            task: item,
            assigned_to: "Sales Team",
            deadline: "Immediate"
          })));
          allSuggestions.push(...immediateActions);
        }
        if (longTermActions.length > 0) {
          allActionItems.push(...longTermActions.map(item => ({
            task: item,
            assigned_to: "Sales Team",
            deadline: "Long-term"
          })));
          allSuggestions.push(...longTermActions);
        }

        // Suggestions for salesperson (new format)
        const suggestions = normalizeArray(parsedPhase.suggestions_for_salesperson);
        if (suggestions.length > 0) {
          allSuggestions.push(...suggestions);
        }

        // Extract keywords (handles both old and new formats)
        if (parsedPhase.keywords_and_entities) {
          // console.log(`Phase ${index + 1} keywords_and_entities:`, parsedPhase.keywords_and_entities);
          const keywords = normalizeArray(parsedPhase.keywords_and_entities?.keywords);
          const competitors = normalizeArray(parsedPhase.keywords_and_entities?.competitors_mentioned);
          const products = normalizeArray(parsedPhase.keywords_and_entities?.products_discussed);
          const features = normalizeArray(parsedPhase.keywords_and_entities?.features_of_interest);

          // console.log("Extracted keywords:", keywords);
          // console.log("Extracted competitors:", competitors);
          // console.log("Extracted products:", products);
          // console.log("Extracted features:", features);

          allKeywords.push(...keywords, ...competitors, ...products, ...features);
        }

        // Extract meeting summary (handles both old and new formats)
        const summary = normalizeValue(parsedPhase.meeting_summary?.summary);
        const intentSummary = normalizeValue(parsedPhase.meeting_intent_summary);

        if (summary) {
          meetingSummary = summary;
        }
        if (intentSummary) {
          if (!meetingSummary) {
            meetingSummary = intentSummary;
          } else {
            meetingSummary += "\n\n" + intentSummary;
          }
        }

        // Q&A pairs (handles multiple formats: qna_timeline, qna_pairs, qna)
        const qnaPairs = parsedPhase.qna_pairs || parsedPhase.qna_timeline || parsedPhase.qna || [];
        // console.log(`Phase ${index + 1} Q&A pairs found:`, qnaPairs.length);
        
        if (Array.isArray(qnaPairs) && qnaPairs.length > 0) {
          // Handle format where questions and answers are in separate objects
          let currentQuestion = null;
          qnaPairs.forEach((qa, qaIndex) => {
            // console.log(`Processing Q&A ${qaIndex}:`, qa);
            
            if (qa.question && qa.answer) {
              // Format where both are in same object (most common)
              const qaItem = {
                question: normalizeValue(qa.question) || "",
                answer: normalizeValue(qa.answer) || "",
                speaker: normalizeValue(qa.speaker) || "Agent",
                time: normalizeValue(qa.timestamp) || normalizeValue(qa.time) || normalizeValue(qa.start_time) || ""
              };
              // console.log(`✅ Added Q&A item:`, qaItem);
              allQaData.push(qaItem);
            } else if (qa.question && !qa.answer) {
              // Store question and wait for answer
              currentQuestion = {
                question: normalizeValue(qa.question) || "",
                time: normalizeValue(qa.timestamp) || normalizeValue(qa.time) || normalizeValue(qa.start_time) || normalizeValue(qa.end_time) || "",
                speaker: normalizeValue(qa.speaker) || "Agent"
              };
            } else if (qa.answer && currentQuestion) {
              // Match answer with previous question
              const qaItem = {
                question: currentQuestion.question,
                answer: normalizeValue(qa.answer) || "",
                speaker: currentQuestion.speaker,
                time: currentQuestion.time
              };
              console.log(`✅ Added paired Q&A item:`, qaItem);
              allQaData.push(qaItem);
              currentQuestion = null;
            }
          });
        }
        console.log(`Phase ${index + 1} total Q&A extracted:`, allQaData.length);

        // Extract topics (handles both topics and topics_timeline formats)
        const topicsData = parsedPhase.topics || parsedPhase.topics_timeline || [];
        if (Array.isArray(topicsData) && topicsData.length > 0) {
          return { topicsData };
        }

      } catch (e) {
        console.error(`Failed to parse phase ${index + 1} response:`, e);
        console.error(`Raw phase response:`, phaseResponse.substring(0, 500));
      }
    });

    // Clean up suggestions - ensure only strings, no [object Object]
    allSuggestions = allSuggestions
      .map(item => {
        if (typeof item === 'string') {
          const trimmed = item.trim();
          // Reject [object Object] strings explicitly
          if (trimmed === '[object Object]' || trimmed === '') return null;
          return trimmed;
        }
        if (typeof item === 'object' && item !== null) {
          // Try to extract string from object
          if (item.text) return String(item.text).trim();
          if (item.value) return String(item.value).trim();
          if (item.suggestion) return String(item.suggestion).trim();
          return null; // Return null for complex objects
        }
        const str = String(item).trim();
        if (str === '[object Object]' || str === '') return null;
        return str;
      })
      .filter(item => item !== null && typeof item === 'string' && item.length > 0); // Only keep non-empty strings

    // Clean up keywords too
    allKeywords = allKeywords
      .filter(k => {
        if (typeof k !== 'string') return false;
        const trimmed = k.trim();
        return trimmed && trimmed !== '[object Object]';
      });

    // Remove duplicates
    allKeywords = [...new Set(allKeywords)];
    allSuggestions = [...new Set(allSuggestions)];

    console.log("=== Final parsed data ===");
    console.log("Insights:", allInsights.length);
    console.log("Action items:", allActionItems.length);
    console.log("Keywords:", allKeywords.length, allKeywords);
    console.log("Suggestions:", allSuggestions.length, allSuggestions);
    console.log("Summary:", meetingSummary ? "Present" : "Empty");

    return {
      insights: allInsights,
      actionItems: allActionItems,
      keywords: allKeywords,
      suggestions: allSuggestions,
      qaData: allQaData,
      meetingSummary,
      topics: [] // Will be extracted separately
    };
  };

  // // Function to parse phase responses (new format)
  // const parsePhaseResponses = (phaseResponses) => {
  //   let allInsights = [];
  //   let allActionItems = [];
  //   let allKeywords = [];
  //   let allSuggestions = [];
  //   let allQaData = [];
  //   let meetingSummary = "";

  //   // Helper function to safely normalize strings/arrays
  //   const normalizeToArray = (value) => {
  //     if (!value) return [];
  //     if (Array.isArray(value)) return value.filter(Boolean);
  //     if (typeof value === "string") return [value.trim()].filter(Boolean);
  //     if (typeof value === "object")
  //       return Object.values(value).filter(Boolean);
  //     return [];
  //   };

  //   // Parse each phase response
  //   Object.values(phaseResponses).forEach((phaseResponse, index) => {
  //     if (!phaseResponse) return;
  //     console.log(
  //       `Processing phase ${ index + 1 }:`,
  //       phaseResponse.substring(0, 200) + "..."
  //     );

  //     try {
  //       // Extract JSON from fenced block
  //       const fenceMatch = phaseResponse.match(
  //         /```(?:json)?\s*([\s\S]*?)\s*```/i
  //       );
  //       let jsonString = fenceMatch
  //         ? fenceMatch[1]
  //         : phaseResponse
  //             .replace(/```/g, "")
  //             .replace(/^\s*json\s*/i, "")
  //             .trim();

  //       console.log(
  //         `Phase ${ index + 1 } JSON string:`,
  //         jsonString.substring(0, 300) + "..."
  //       );

  //       // Fix common JSON issues
  //       let cleanedJsonString = jsonString
  //         .replace(/,\s*}/g, "}") // Remove trailing commas before }
  //         .replace(/,\s*]/g, "]") // Remove trailing commas before ]
  //         .replace(/:\s*i(\d+)/g, ": $1") // Fix i20 → 20
  //         .replace(/:\s*,/g, ": null,"); // Fix empty values

  //       const parsedPhase = JSON.parse(cleanedJsonString);
  //       console.log(`Phase ${ index + 1 } parsed data:`, parsedPhase);

  //       // ---- CUSTOMER PROFILING ----
  //       if (parsedPhase?.customer_profiling) {
  //         const painPoints = normalizeToArray(
  //           parsedPhase.customer_profiling.pain_points
  //         );
  //         const goals = normalizeToArray(parsedPhase.customer_profiling.goals);

  //         if (painPoints.length)
  //           allInsights.push({ header: "Pain Points", points: painPoints });

  //         if (goals.length)
  //           allInsights.push({ header: "Customer Goals", points: goals });
  //       }

  //       // ---- DECISION DRIVERS ----
  //       const decisionDrivers = normalizeToArray(parsedPhase?.decision_drivers);
  //       if (decisionDrivers.length)
  //         allInsights.push({
  //           header: "Decision Drivers",
  //           points: decisionDrivers,
  //         });

  //       // ---- DEAL HEALTH ----
  //       if (parsedPhase?.deal_health) {
  //         const positiveSignals = normalizeToArray(
  //           parsedPhase.deal_health.positive_signals
  //         );
  //         const negativeSignals = normalizeToArray(
  //           parsedPhase.deal_health.negative_signals
  //         );

  //         if (positiveSignals.length)
  //           allInsights.push({
  //             header: "Positive Signals",
  //             points: positiveSignals,
  //           });

  //         if (negativeSignals.length)
  //           allInsights.push({
  //             header: "Negative Signals",
  //             points: negativeSignals,
  //           });
  //       }

  //       // ---- RISK ANALYSIS ----
  //       const riskFactors = normalizeToArray(
  //         parsedPhase?.risk_analysis?.risk_factors
  //       );
  //       if (riskFactors.length)
  //         allInsights.push({ header: "Risk Factors", points: riskFactors });

  //       // ---- STRATEGIC INSIGHTS ----
  //       if (parsedPhase?.strategic_insights) {
  //         const oppRisks = normalizeToArray(
  //           parsedPhase.strategic_insights.opportunity_risks
  //         );
  //         const salesOpps = normalizeToArray(
  //           parsedPhase.strategic_insights.sales_opportunities
  //         );

  //         if (oppRisks.length)
  //           allInsights.push({ header: "Opportunity Risks", points: oppRisks });

  //         if (salesOpps.length)
  //           allInsights.push({
  //             header: "Sales Opportunities",
  //             points: salesOpps,
  //           });
  //       }

  //       // ---- ACTION ITEMS ----
  //       const salesActionItems = normalizeToArray(
  //         parsedPhase?.action_items?.sales_action_items
  //       );
  //       const customerActionItems = normalizeToArray(
  //         parsedPhase?.action_items?.customer_action_items
  //       );
  //       const dueDate =
  //         parsedPhase?.action_items?.due_dates?.onboarding_session || "";

  //       if (salesActionItems.length) {
  //         allActionItems.push(
  //           ...salesActionItems.map((item) => ({
  //             task: item,
  //             assigned_to: "Sales Team",
  //             deadline: dueDate,
  //           }))
  //         );
  //       }

  //       if (customerActionItems.length) {
  //         allActionItems.push(
  //           ...customerActionItems.map((item) => ({
  //             task: item,
  //             assigned_to: "Customer",
  //             deadline: dueDate,
  //           }))
  //         );
  //       }

  //       // ---- NEXT STEPS ----
  //       const immediateActions = normalizeToArray(
  //         parsedPhase?.next_steps?.immediate_actions
  //       );
  //       const longTermActions = normalizeToArray(
  //         parsedPhase?.next_steps?.long_term_actions
  //       );

  //       if (immediateActions.length) {
  //         allActionItems.push(
  //           ...immediateActions.map((item) => ({
  //             task: item,
  //             assigned_to: "Sales Team",
  //             deadline: "Immediate",
  //           }))
  //         );
  //         allSuggestions.push(...immediateActions);
  //       }

  //       if (longTermActions.length) {
  //         allActionItems.push(
  //           ...longTermActions.map((item) => ({
  //             task: item,
  //             assigned_to: "Sales Team",
  //             deadline: "Long-term",
  //           }))
  //         );
  //         allSuggestions.push(...longTermActions);
  //       }

  //       // ---- KEYWORDS ----
  //       const keywords = [
  //         ...normalizeToArray(parsedPhase?.keywords_and_entities?.keywords),
  //         ...normalizeToArray(
  //           parsedPhase?.keywords_and_entities?.competitors_mentioned
  //         ),
  //         ...normalizeToArray(
  //           parsedPhase?.keywords_and_entities?.products_discussed
  //         ),
  //       ];
  //       if (keywords.length) allKeywords.push(...keywords);

  //       // ---- SUMMARY ----
  //       const summaryPart = parsedPhase?.meeting_summary?.summary;
  //       const intentPart = parsedPhase?.meeting_intent_summary;

  //       if (summaryPart)
  //         meetingSummary += (meetingSummary ? "\n\n" : "") + summaryPart;
  //       if (intentPart)
  //         meetingSummary += (meetingSummary ? "\n\n" : "") + intentPart;
  //     } catch (e) {
  //       console.error(`❌ Failed to parse phase ${ index + 1 } response:`, e);
  //       console.error(`Raw phase response:`, phaseResponse.substring(0, 500));
  //     }
  //   });

  //   // Remove duplicates
  //   allKeywords = [...new Set(allKeywords)];
  //   allSuggestions = [...new Set(allSuggestions)];

  //   console.log("✅ Final parsed data summary:", {
  //     insights: allInsights.length,
  //     actionItems: allActionItems.length,
  //     keywords: allKeywords.length,
  //     suggestions: allSuggestions.length,
  //     summary: meetingSummary ? "Present" : "Empty",
  //   });

  //   return {
  //     insights: allInsights,
  //     actionItems: allActionItems,
  //     keywords: allKeywords,
  //     suggestions: allSuggestions,
  //     qaData: allQaData,
  //     meetingSummary,
  //   };
  // };

  // Function to parse legacy response (old format)
  const parseLegacyResponse = (responseString) => {
    const fenceMatch = responseString.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const jsonString = fenceMatch
      ? fenceMatch[1]
      : responseString
        .replace(/```/g, "")
        .replace(/^\s*json\s*/i, "")
        .trim();

    const parsedData = JSON.parse(jsonString);
    const minutes = parsedData.meeting_minutes || {};

    const mergedActionItems = [
      ...(Array.isArray(minutes.action_items) ? minutes.action_items : []),
      ...(Array.isArray(minutes.decisions_made) ? minutes.decisions_made : []),
    ];

    const questionsAndAnswers = Array.isArray(minutes.questions_and_answers)
      ? minutes.questions_and_answers
      : Array.isArray(parsedData.questions_and_answers)
        ? parsedData.questions_and_answers
        : [];

    return {
      insights: Array.isArray(minutes.insights) ? minutes.insights : [],
      actionItems: mergedActionItems,
      keywords: Array.isArray(minutes.keywords) ? minutes.keywords : [],
      qaData: questionsAndAnswers,
      meetingSummary:
        minutes.meeting_summary || parsedData.meeting_summary || "",
    };
  };

  const getMeetingDetails = async () => {
    if (isLiveMeeting && detailsLoading) {
      // console.log("Skipping call: already loading (live meeting)...");
      return;
    }

    try {
      setDetailsLoading(true);
      // console.log("=== getMeetingDetails called for ID:", id);
      const response = await apiRequest(
        "get",
        `trans/generate-sales-coach/${id}`
      );
      // console.log("=== Meeting details API response:", response);
      // console.log("Response type:", response?.responseType);
      // console.log("Phase responses keys:", response?.phaseResponses ? Object.keys(response.phaseResponses) : 'none');

      // Handle new phase responses or legacy response
      if (
        response &&
        response.responseType === "phase" &&
        response.phaseResponses
      ) {
        // console.log("=== Parsing phase responses ===");
        // Handle new phase response format
        const parsedData = parsePhaseResponses(response.phaseResponses);
        // console.log("=== Parsed phase data:", parsedData);
        // console.log("=== Keywords from parsed data:", parsedData.keywords);

        setMeetingDetails(parsedData);
        setInsights(parsedData.insights);
        setActionItems(parsedData.actionItems);
        setKeywords(parsedData.keywords || []);
        setSuggestions(parsedData.suggestions);
        setMeetingSummary(parsedData.meetingSummary);
        // console.log("Keywords set:", parsedData.keywords);

        // Set Q&A data
        if (parsedData.qaData && parsedData.qaData.length > 0) {
          // console.log("Setting Q&A data:", parsedData.qaData);
          setQaData(parsedData.qaData);
        }

        // Extract and process topics from phase responses
        let extractedTopics = [];
        let extractedQuestions = [];
        Object.values(response.phaseResponses).forEach((phaseResponse) => {
          try {
            const fenceMatch = phaseResponse.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
            const jsonString = fenceMatch ? fenceMatch[1] : phaseResponse.replace(/```/g, "").trim();
            const parsedPhase = JSON.parse(jsonString);

            // Extract topics
            const topicsData = parsedPhase.topics || parsedPhase.topics_timeline || [];
            if (Array.isArray(topicsData) && topicsData.length > 0) {
              topicsData.forEach((topic, index) => {
                const startTime = timestampToSeconds(topic.start_time || topic.start_timestamp || "0:00");
                const endTime = timestampToSeconds(topic.end_time || topic.end_timestamp || "0:00");
                if (startTime >= 0 && endTime > startTime) {
                  extractedTopics.push({
                    name: topic.topic || "Untitled Topic",
                    startTime: startTime,
                    endTime: endTime,
                    color: topicColors[extractedTopics.length % topicColors.length]
                  });
                }
              });
            }

            // Extract standalone questions from qna array
            const qnaData = parsedPhase.qna || [];
            if (Array.isArray(qnaData) && qnaData.length > 0) {
              qnaData.forEach(item => {
                if (item.question && !item.answer) {
                  extractedQuestions.push({
                    question: item.question,
                    time: item.start_time || item.timestamp || "",
                    speaker: item.speaker || "Unknown"
                  });
                }
              });
            }
          } catch (e) {
            console.error("Error extracting topics/questions:", e);
          }
        });

        if (extractedTopics.length > 0) {
          console.log("Extracted topics:", extractedTopics);
          setTopics(extractedTopics);
        }

        if (extractedQuestions.length > 0) {
          console.log("Extracted questions:", extractedQuestions);
          setQuestions(extractedQuestions);
        }
      } else if (
        response &&
        response.responseType === "legacy" &&
        response.response
      ) {
        // Handle legacy response format
        const parsedData = parseLegacyResponse(response.response);
        console.log("Parsed legacy data:", parsedData);

        setMeetingDetails(parsedData);
        setInsights(parsedData.insights);
        setActionItems(parsedData.actionItems);
        setKeywords(parsedData.keywords || []);
        setSuggestions(parsedData.suggestions || []);
        setMeetingSummary(parsedData.meetingSummary);
        console.log("Keywords set (legacy):", parsedData.keywords);

        // Set Q&A data
        if (parsedData.qaData && parsedData.qaData.length > 0) {
          console.log("Setting Q&A data (legacy):", parsedData.qaData);
          setQaData(parsedData.qaData);
        }
      } else {
        // Fallback for any unexpected format
        console.warn("Unexpected response format:", response);
        setMeetingDetails({});
        setInsights([]);
        setActionItems([]);
        setKeywords([]);
        setSuggestions([]);
        setMeetingSummary("Unable to parse meeting details");
        setQaData([]);
      }

      // Set meeting metadata from API wrapper fields regardless of parsing
      setMeetingTitle(response?.title || response?.meetingName || "Meeting");
      setMeetingName(response?.meetingName || response?.title || "");
      setMeetingStartTime(response?.startTime || "");
      setMeetingEndTime(response?.endTime || "");
      setCreatedBy(response?.createBy || "");
    } catch (error) {
      console.error("Error fetching meeting details:", error);
      // Set default values on API error
      setMeetingDetails({});
      setInsights([]);
      setActionItems([]);
      setKeywords([]);
      setSuggestions([]);
      setMeetingSummary("Unable to load meeting details");
      setQaData([]);
      setMeetingTitle("Meeting");
      setMeetingStartTime("");
      setMeetingEndTime("");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      console.log("=== useEffect fetchData called, isLiveMeeting:", isLiveMeeting);
      // Fetch meeting details only for non-live meetings
      if (!isLiveMeeting) {
        console.log("=== Calling getMeetingDetails ===");
        await getMeetingDetails();
      } else {
        console.log("=== Skipping getMeetingDetails (live meeting) ===");
      }
      // Always fetch transcript to get recording URL
      await getTranscript();
      await calculateTalktimeRatio();
    };

    fetchData();
  }, [id]);

  function openPopup() {
    const googleMeetUrl = `https://meet.google.com/${id}`; // Replace with actual meeting link if dynamic
    window.open(
      googleMeetUrl,
      "GoogleMeetPopup",
      "width=800,height=600,top=100,left=100"
    );
  }

  return (
    <Box className="main-container">
      {/* Live Meeting Popup Button */ }
      { isLiveMeeting && (
        <Box className="live-meeting-button">
          <Button
            variant="contained"
            color="primary"
            startIcon={ <FiMaximize2 /> }
            onClick={ openPopup }
            sx={ { borderRadius: 3, fontWeight: 600, px: 3, py: 1 } }
          >
            Open Live Meeting
          </Button>
        </Box>
      ) }

      <Box className="content-container" sx={ { gap: 0 } }>
        {/* Left: Summary, key points, speakers, now tabbed */ }
        <Box className="left-column">
          <Paper elevation={ 0 } className="meeting-header">
            {/* Meeting Name */ }
            <Box display="flex" alignItems="center" gap={ 1 } mb={ 1 }>
              <Typography variant="h5" fontWeight={ 600 } color="#202124">
                { meetingName || meetingTitle || "Meeting" }
              </Typography>
              { isLiveMeeting && (
                <Chip
                  label="LIVE"
                  size="small"
                  className="live-chip"
                />
              ) }
            </Box>

            {/* Meeting Metadata Row */ }
            <Box display="flex" alignItems="center" gap={ 1.5 } mb={ 3 } flexWrap="wrap">
              {/* Date */ }
              { meetingStartTime && (
                <Box display="flex" alignItems="center" gap={ 0.5 }>
                  <FiCalendar size={ 14 } color="#5f6368" />
                  <Typography fontSize={ 13 } color="#5f6368">
                    { new Date(meetingStartTime).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }) }
                  </Typography>
                </Box>
              ) }

              {/* Time */ }
              { meetingStartTime && (
                <Box display="flex" alignItems="center" gap={ 0.5 }>
                  <FiClock size={ 14 } color="#5f6368" />
                  <Typography fontSize={ 13 } color="#5f6368">
                    { new Date(meetingStartTime).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    }) }
                  </Typography>
                </Box>
              ) }

              {/* Participants */ }
              <Box display="flex" alignItems="center" gap={ 0.5 }>
                <FiUsers size={ 14 } color="#5f6368" />
                <Typography fontSize={ 13 } color="#5f6368">
                  { totalSpeakers || Object.keys(speakersfr?.speakerPercentages || {}).length || 0 }
                </Typography>
              </Box>

              {/* Created By */ }
              { createdBy && (
                <Box display="flex" alignItems="center" gap={ 0.5 }>
                  <FiUser size={ 14 } color="#5f6368" />
                  <Typography fontSize={ 13 } color="#5f6368">
                    By { createdBy }
                  </Typography>
                </Box>
              ) }
            </Box>

            {/* Video Player */ }
            <Box mb={ 3 }>
              <VideoPlayer
                videoUrl={ videoUrl }
                audioUrl={ recordingUrl }
                transcript={ currentTranscript }
                topics={ topics }
                onTranscriptSync={ (index, currentTime, duration) => {
                  // This callback handles both state updates and scrolling
                  if (index !== -1) setActiveTranscriptIndex(index);
                  if (currentTime) setCurrentTime(currentTime);
                  if (duration) {
                    setMeetingDuration(duration);
                  }

                  // Scroll transcript to active item
                  if (index >= 0) {
                    const transcriptElement = document.getElementById(`transcript-item-${index}`);
                    if (transcriptElement) {
                      transcriptElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }
                } }
              />
            </Box>



            {/* Tabs for left column */ }
            <Box className="tab-container">
              <Tabs
                value={ tab }
                onChange={ handleTabChange }
                aria-label="Meeting details tabs"
                sx={ { minHeight: 44 } }
              >
                <Tab
                  label={ <span className="tab-label">Insights</span> }
                  sx={ { minWidth: 120, minHeight: 44 } }
                />
                <Tab
                  label={ <span className="tab-label">Action Items</span> }
                  sx={ { minWidth: 140, minHeight: 44 } }
                />
                <Tab
                  label={ <span className="tab-label">Q&A</span> }
                  sx={ { minWidth: 120, minHeight: 44 } }
                />
                <Tab
                  label={ <span className="tab-label">Keywords</span> }
                  sx={ { minWidth: 120, minHeight: 44 } }
                />
                <Tab
                  label={ <span className="tab-label">Suggestions</span> }
                  sx={ { minWidth: 140, minHeight: 44 } }
                />
                {/* <Tab
                  label={<span style={{ fontWeight: 700 }}>Activity Log</span>}
                  sx={{ minWidth: 140, minHeight: 44 }}
                /> */}
              </Tabs>
            </Box>
            {/* Tab Panels for left column */ }
            { tab === 0 && (
              <Box px={ 1.5 } pt={ 1 } pb={ 1.5 }>
                {/* Speakers Section */ }
                { speakersfr?.speakerPercentages && Object.keys(speakersfr.speakerPercentages).length > 0 && (
                  <Box mb={ 1.5 }>
                    <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                      Speakers
                    </Typography>
                    <Stack spacing={ 1.5 }>
                      { Object.keys(speakersfr?.speakerPercentages).map(
                        (speaker, index) => (
                          <TalkTimeRatio
                            key={ speaker }
                            name={
                              speaker === "null" ? "Unknown Speaker" : speaker
                            }
                            time={ speakersfr?.speakerTimesFormatted[speaker] }
                            percentage={ speakersfr.speakerPercentages[speaker] }
                            color={ generateSpeakerColor(speaker) }
                            transcript={ currentTranscript }
                            totalDuration={ meetingDuration }
                          />
                        )
                      ) }
                    </Stack>
                  </Box>
                ) }

                {/* Insights Section */ }
                <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                  Key Insights
                </Typography>
                { currentInsights.length > 0 ? (
                  currentInsights.map((insight, idx) => (
                    <Box key={ idx } mb={ 2 }>
                      <Typography fontWeight={ 600 } fontSize={ 15 } mb={ 0.5 } color="#1e293b">
                        { insight.header }
                      </Typography>
                      <ul style={ { margin: 0, paddingLeft: 20, lineHeight: 1.5 } }>
                        { insight.points.map((pt, i) => (
                          <li
                            key={ i }
                            style={ {
                              color: "#555",
                              fontSize: 14,
                              marginBottom: 4,
                            } }
                          >
                            { pt }
                          </li>
                        )) }
                      </ul>
                    </Box>
                  ))
                ) : (
                  <Typography color="#888" textAlign="center" py={ 2 }>
                    No insights available for this meeting.
                  </Typography>
                ) }

                {/* Keywords Section */ }
                { detailsLoading ? (
                  <Box display="flex" justifyContent="center" py={ 2 }>
                    <CircularProgress size={ 24 } />
                  </Box>
                ) : currentKeywords.length > 0 ? (
                  <Box mt={ 1.5 }>
                    <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                      Keywords
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={ 1 }>
                      { currentKeywords.map((keyword, idx) => (
                        <Chip
                          key={ idx }
                          label={ keyword }
                          size="small"
                          sx={ {
                            background: generateSpeakerColor(keyword),
                            color: "white",
                            fontWeight: 600,
                          } }
                        />
                      )) }
                    </Box>
                  </Box>
                ) : null }

                {/* Meeting Summary */ }
                { currentSummary && (
                  <Box mt={ 1.5 }>
                    <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                      Meeting Summary
                    </Typography>
                    <Typography fontSize={ 14 } color="#555" lineHeight={ 1.5 }>
                      { currentSummary }
                    </Typography>
                  </Box>
                ) }
              </Box>
            ) }
            { tab === 1 && (
              <Box px={ 1.5 } pt={ 1 } pb={ 1.5 }>
                <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                  Action Items
                </Typography>
                { currentActionItems.length > 0 ? (
                  <Box>
                    { currentActionItems.map((item, idx) => (
                      <Box
                        key={ idx }
                        mb={ 1.5 }
                        display="flex"
                        alignItems="flex-start"
                        gap={ 1 }
                      >
                        <Box flex={ 1 }>
                          <Typography fontSize={ 14 } color="#555" lineHeight={ 1.5 }>
                            <strong style={ { color: "#1e293b" } }>{ item.assigned_to }</strong> - { item.task }{ " " }
                            <span style={ { color: "#888" } }>(Due: { item.deadline })</span>
                          </Typography>
                        </Box>
                        <Box display="flex" gap={ 1 } flexShrink={ 0 }>
                          <Button
                            variant="outlined"
                            size="small"
                            style={ {
                              borderRadius: 12,
                              fontWeight: 600,
                              color: "#396afc",
                              borderColor: "#396afc",
                              fontSize: 12,
                              padding: "4px 12px",
                            } }
                            onClick={ () => handleAddToCalendar(item) }
                          >
                            Calendar
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            style={ {
                              borderRadius: 12,
                              fontWeight: 600,
                              color: "#43cea2",
                              borderColor: "#43cea2",
                              fontSize: 12,
                              padding: "4px 12px",
                            } }
                            onClick={ () => handleAddToTask(item) }
                          >
                            Task
                          </Button>
                        </Box>
                      </Box>
                    )) }
                  </Box>
                ) : (
                  <Typography color="#888" textAlign="center" py={ 2 }>
                    No action items available for this meeting.
                  </Typography>
                ) }
              </Box>
            ) }
            { tab === 2 && (
              <Box px={ 1.5 } pt={ 1 } pb={ 1.5 }>
                <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                  Questions & Answers
                </Typography>
                { currentQaData.length > 0 ? (
                  currentQaData.map((qa, idx) => (
                    <Box
                      key={ idx }
                      mb={ 2 }
                      p={ 2 }
                      sx={ {
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        background: "#fafafa",
                      } }
                    >
                      <Typography
                        fontWeight={ 600 }
                        fontSize={ 14 }
                        color="#396afc"
                        mb={ 0.5 }
                        lineHeight={ 1.5 }
                      >
                        Q: { qa.question || "No question provided" }
                      </Typography>
                      <Typography fontSize={ 14 } color="#555" lineHeight={ 1.5 }>
                        A: { qa.answer || "No answer provided" }
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="#888" textAlign="center" py={ 2 }>
                    No Q&A available for this meeting.
                  </Typography>
                ) }
              </Box>
            ) }
            { tab === 3 && (
              <Box px={ 1.5 } pt={ 1 } pb={ 1.5 }>
                <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                  Keywords
                </Typography>
                { detailsLoading ? (
                  <Box display="flex" justifyContent="center" py={ 2 }>
                    <CircularProgress size={ 24 } />
                  </Box>
                ) : currentKeywords.length > 0 ? (
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={ 1 }
                  >
                    { currentKeywords.map((keyword, idx) => (
                      <Chip
                        key={ idx }
                        label={ keyword }
                        size="medium"
                        sx={ {
                          background: generateSpeakerColor(keyword),
                          color: "white",
                          fontWeight: 600,
                        } }
                      />
                    )) }
                  </Box>
                ) : (
                  <Typography color="#888" textAlign="center" py={ 2 }>
                    No keywords available for this meeting.
                  </Typography>
                ) }
              </Box>
            ) }
            { tab === 4 && (
              <Box px={ 1.5 } pt={ 1 } pb={ 1.5 }>
                <Typography variant="h6" fontWeight={ 700 } mb={ 0.5 }>
                  Suggestions
                </Typography>
                { currentSuggestions.length > 0 ? (
                  <ul style={ { margin: 0, paddingLeft: 20, lineHeight: 1.5 } }>
                    { currentSuggestions.map((suggestion, idx) => (
                      <li
                        key={ idx }
                        style={ {
                          color: "#555",
                          fontSize: 14,
                          marginBottom: 4,
                        } }
                      >
                        { suggestion }
                      </li>
                    )) }
                  </ul>
                ) : (
                  <Typography color="#888" textAlign="center" py={ 2 }>
                    No suggestions available for this meeting.
                  </Typography>
                ) }
              </Box>
            ) }
            { tab === 6 && (
              <Box p={ 4 } textAlign="center" color="#888">
                <Typography variant="h6" fontWeight={ 700 } mb={ 2 }>
                  Activity Log
                </Typography>
                <Typography>No activity log found for this meeting.</Typography>
              </Box>
            ) }
          </Paper>
        </Box>
        {/* Right: Transcript - Split vertically if live meeting */ }
        { isLiveMeeting ? (
          <Box flex={ 3 } display="flex" flexDirection="column" gap={ 2 }>
            {/* Top half: Live transcript */ }
            <Box flex={ 1 } bgcolor="#fff" borderRadius={ 3 } p={ 3 }>
              <Box display="flex" alignItems="center" mb={ 2 }>
                <Typography variant="h6" fontWeight={ 700 } flex={ 1 }>
                  Live Transcript
                </Typography>
                <Box display="flex" alignItems="center" gap={ 2 }>
                  {/* Language Toggle for Live Transcript - Commented for now */ }
                  {/* <ToggleButtonGroup
                    value={ transcriptLanguage }
                    exclusive
                    onChange={ handleLanguageChange }
                    aria-label="transcript language"
                    size="small"
                    className="language-toggle"
                  >
                    <ToggleButton value="english" aria-label="english">
                      English
                    </ToggleButton>
                    <ToggleButton value="hindi" aria-label="hindi">
                      Hindi
                    </ToggleButton>
                  </ToggleButtonGroup> */}
                  <Chip
                    label="LIVE"
                    size="small"
                    sx={ {
                      background: "#ff4d4d",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    } }
                  />
                </Box>
              </Box>
              <Divider sx={ { mb: 2 } } />
              <Box
                ref={ transcriptRef }
                sx={ {
                  maxHeight: 300,
                  overflowY: "auto",
                  scrollBehavior: "smooth",
                } }
              >
                { isLiveLoading ? (
                  <Box display="flex" justifyContent="center" py={ 2 }>
                    <CircularProgress size={ 24 } />
                  </Box>
                ) : currentTranscript && currentTranscript.length > 0 ? (
                  <List>
                    { currentTranscript.map((t, i) => {
                      const isActive = i === activeTranscriptIndex;
                      return (
                        <ListItem
                          alignItems="flex-start"
                          key={ i }
                          id={ `transcript-item-${i}` }
                          sx={ {
                            background: isActive ? "rgba(57, 106, 252, 0.1)" : "transparent",
                            borderRadius: 2,
                            mb: 1,
                            transition: "all 0.3s ease",
                            border: isActive ? "2px solid var(--primary-color)" : "2px solid transparent"
                          } }
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={ {
                                bgcolor: generateSpeakerColor(t.speaker || "Unknown"),
                                border: isActive ? "2px solid var(--primary-color)" : "none"
                              } }
                            >
                              { (t?.speaker || "U").charAt(0).toUpperCase() }
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <span style={ { fontWeight: 700 } }>
                                { capitalizeWords(t.speaker || "Unknown") }{ " " }
                                <span
                                  style={ { color: "#b0b7c3", fontWeight: 400 } }
                                >
                                  { formatTranscriptTime(t.absolute_start_time) }
                                </span>
                              </span>
                            }
                            secondary={
                              <Box>
                                { t.words && t.words.length > 0 ? (
                                  <WordHighlighter 
                                    words={ t.words } 
                                    isActive={ isActive } 
                                    currentTime={ currentTime } 
                                    timingOffset={ timingOffset }
                                  />
                                ) : (
                                  <Typography
                                    sx={ {
                                      color: isActive ? "var(--primary-color)" : "inherit",
                                      fontWeight: isActive ? 600 : 400,
                                      fontSize: isActive ? 15 : 14
                                    } }
                                  >
                                    { t.text || "No text available" }
                                  </Typography>
                                ) }
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    }) }
                  </List>
                ) : (
                  <Box textAlign="center" py={ 4 }>
                    <Typography color="#888" fontSize={ 16 } mb={ 1 }>
                      Transcript not available
                    </Typography>
                    <Typography color="#aaa" fontSize={ 14 }>
                      { isLiveLoading ? "Loading transcript..." : "No transcript data found for this meeting" }
                    </Typography>
                  </Box>
                ) }
                {/* Typing indicator */ }
                { isTyping && (
                  <Box display="flex" alignItems="center" gap={ 1 } p={ 2 }>
                    <Box
                      sx={ {
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#396afc",
                        animation: "pulse 1.5s infinite",
                      } }
                    />
                    <Box
                      sx={ {
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#396afc",
                        animation: "pulse 1.5s infinite",
                        animationDelay: "0.2s",
                      } }
                    />
                    <Box
                      sx={ {
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#396afc",
                        animation: "pulse 1.5s infinite",
                        animationDelay: "0.4s",
                      } }
                    />
                    <Typography color="#396afc" fontSize={ 14 } ml={ 1 }>
                      Someone is speaking...
                    </Typography>
                  </Box>
                ) }
              </Box>
            </Box>
            {/* Bottom half: Live Q&A */ }
            <Box flex={ 1 } bgcolor="#fff" borderRadius={ 3 } p={ 3 }>
              <Box display="flex" alignItems="center" mb={ 2 }>
                <Typography variant="h6" fontWeight={ 700 } flex={ 1 }>
                  Live Q&A
                </Typography>
                <Chip
                  label="LIVE"
                  size="small"
                  sx={ {
                    background: "#ff4d4d",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  } }
                />
              </Box>
              <Divider sx={ { mb: 2 } } />
              <Box sx={ { maxHeight: 300, overflowY: "auto" } }>
                { currentQaData.length > 0 ? (
                  currentQaData.map((qa, idx) => (
                    <Box
                      key={ idx }
                      mb={ 3 }
                      p={ 2 }
                      sx={ {
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        background: "#fafafa",
                      } }
                    >
                      <Box display="flex" alignItems="center" gap={ 1 } mb={ 1 }>
                        <Avatar
                          sx={ {
                            bgcolor: "#396afc",
                            width: 24,
                            height: 24,
                            fontSize: 12,
                          } }
                        >
                          { qa.speaker }
                        </Avatar>
                        <Typography fontWeight={ 600 } fontSize={ 14 }>
                          { qa.speaker }
                        </Typography>
                        <Typography color="#888" fontSize={ 12 }>
                          { qa.time }
                        </Typography>
                      </Box>
                      <Typography
                        fontWeight={ 600 }
                        fontSize={ 14 }
                        color="#396afc"
                        mb={ 1 }
                      >
                        Q: { qa.question }
                      </Typography>
                      <Typography fontSize={ 14 } color="#555">
                        A: { qa.answer }
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="#888" textAlign="center">
                    No Q&A available yet.
                  </Typography>
                ) }
              </Box>
            </Box>
          </Box>
        ) : (
          <Box flex={ 3 } bgcolor="#fff" borderRadius={ 3 } p={ 0 } minHeight={ 400 }>
            <Box display="flex" alignItems="center" mb={ 0 } px={ 2 } pt={ 1.5 } pb={ 1.5 } borderBottom="1px solid #e0e0e0" borderRight="1px solid #e0e0e0">
              <Typography variant="h5" fontWeight={ 700 } flex={ 1 }>
                Transcript
              </Typography>
              
              <TextField
                placeholder="Search transcripts..."
                size="small"
                value={ searchQuery }
                onChange={ (e) => handleSearch(e.target.value) }
                InputProps={ {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FiSearch style={ { color: "#b0b7c3" } } />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={ clearSearch }>
                        <FiX style={ { color: "#b0b7c3", fontSize: 16 } } />
                      </IconButton>
                    </InputAdornment>
                  ),
                  style: { borderRadius: 10, background: "#f4f5fa" },
                } }
                style={ { minWidth: 220 } }
              />
            </Box>

            {/* Tabs for transcript section */ }
            <Box
              sx={ {
                borderBottom: 1,
                borderRight: 1,
                borderColor: "divider",
                bgcolor: "#fafbfc",
                px: 0,
                pt: 1,
                mb: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              } }
            >
              <Tabs
                value={ transcriptTab }
                onChange={ handleTranscriptTabChange }
                aria-label="Transcript tabs"
                sx={ { minHeight: 36 } }
              >
                <Tab
                  label={
                    <span style={ { fontWeight: 600, fontSize: 14 } }>
                      Transcript
                    </span>
                  }
                  sx={ { minWidth: 100, minHeight: 36 } }
                />
                <Tab
                  label={
                    <span style={ { fontWeight: 600, fontSize: 14 } }>Q&A</span>
                  }
                  sx={ { minWidth: 80, minHeight: 36 } }
                />
              </Tabs>
              
              {/* Language Toggle Button in Tab Row - Commented for now */ }
              {/* <Box pr={ 2 }>
                <ToggleButtonGroup
                  value={ transcriptLanguage }
                  exclusive
                  onChange={ handleLanguageChange }
                  aria-label="transcript language"
                  size="small"
                  sx={ {
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: 0,
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#5f6368',
                      background: 'transparent',
                      minWidth: '65px',
                      '&.Mui-selected': {
                        backgroundColor: '#396afc',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#2c5ce6',
                        },
                      },
                      '&:hover:not(.Mui-selected)': {
                        backgroundColor: '#f8f9fa',
                        color: '#396afc',
                      },
                      '&:not(:last-of-type)': {
                        borderRight: '1px solid #e0e0e0',
                      },
                      '&.Mui-selected:not(:last-of-type)': {
                        borderRight: '1px solid #396afc',
                      },
                    },
                  } }
                >
                  <ToggleButton value="english" aria-label="english">
                    English
                  </ToggleButton>
                  <ToggleButton value="hindi" aria-label="hindi">
                    Hindi
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box> */}
            </Box>
            {/* Tab content for transcript section */ }
            { transcriptTab === 0 && (
              <Box sx={ { maxHeight: 750, overflowY: "auto", px: 0, pb: 2, pt: 0 } }>
                { searchQuery && (
                  <Box mb={ 2 } p={ 1 } bgcolor="#f0f7ff" borderRadius={ 1 }>
                    <Typography fontSize={ 12 } color="#396afc">
                      { searchResults.length } result{ searchResults.length !== 1 ? 's' : '' } for "{ searchQuery }"
                    </Typography>
                  </Box>
                ) }
                
                {/* Language Indicator */ }
                { transcriptLanguage === 'hindi' && (
                  <Box mb={ 2 } p={ 1 } bgcolor="#fff3cd" borderRadius={ 1 } border="1px solid #ffeaa7">
                    <Typography fontSize={ 12 } color="#856404" display="flex" alignItems="center" gap={ 1 }>
                      🌐 Transcript language: Hindi (हिंदी)
                      <Typography fontSize={ 10 } color="#6c757d" ml={ 1 }>
                        Translation feature coming soon
                      </Typography>
                    </Typography>
                  </Box>
                ) }
                { transcriptLoading ? (
                  <Box display="flex" justifyContent="center" py={ 2 }>
                    <CircularProgress size={ 24 } />
                  </Box>
                ) : currentTranscript && currentTranscript.length > 0 ? (
                  <List>
                    { (searchQuery ? searchResults : currentTranscript).map((t, i) => {
                      const actualIndex = searchQuery ? t.originalIndex : i;
                      const isActive = actualIndex === activeTranscriptIndex;
                      const isSearchResult = searchQuery && searchResults.includes(t);
                      return (
                        <ListItem
                          alignItems="flex-start"
                          key={ searchQuery ? `search-${actualIndex}` : i }
                          id={ `transcript-item-${actualIndex}` }
                          sx={ {
                            background: isActive ? "rgba(57, 106, 252, 0.1)" :
                              isSearchResult ? "rgba(255, 235, 59, 0.1)" : "transparent",
                            borderRadius: 2,
                            mb: 1,
                            transition: "all 0.3s ease",
                            border: isActive ? "2px solid var(--primary-color)" :
                              isSearchResult ? "2px solid #ffc107" : "2px solid transparent",
                            cursor: "pointer",
                            "&:hover": {
                              background: "rgba(57, 106, 252, 0.05)"
                            }
                          } }
                          onClick={ () => {
                            // Jump to transcript time in video
                            const videoElement = document.querySelector('video');
                            if (videoElement && t.start_time) {
                              videoElement.currentTime = t.start_time;
                            }
                          } }
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={ {
                                bgcolor: generateSpeakerColor(t.speaker || "Unknown"),
                                border: isActive ? "2px solid var(--primary-color)" : "none"
                              } }
                            >
                              { (t?.speaker || "U").charAt(0).toUpperCase() }
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            secondaryTypographyProps={ { component: 'div' } }
                            primary={
                              <span style={ { fontWeight: 700 } }>
                                { capitalizeWords(t.speaker || "Unknown") }{ " " }
                                <span
                                  style={ { color: "#b0b7c3", fontWeight: 400 } }
                                >
                                  { formatTranscriptTime(t.absolute_start_time) }
                                </span>
                              </span>
                            }
                            secondary={
                              <Box>
                                { t.words && t.words.length > 0 ? (
                                  <>
                                    <WordHighlighter 
                                      words={ t.words } 
                                      isActive={ isActive } 
                                      searchQuery={ searchQuery } 
                                      currentTime={ currentTime } 
                                      timingOffset={ timingOffset }
                                    />
                                  </>
                                ) : (
                                  <Typography
                                    sx={ {
                                      color: isActive ? "var(--primary-color)" : "inherit",
                                      fontWeight: isActive ? 600 : 400,
                                      fontSize: isActive ? 15 : 14
                                    } }
                                    dangerouslySetInnerHTML={ {
                                      __html: searchQuery ? (t.text || "No text available").replace(
                                        new RegExp(`(${searchQuery})`, 'gi'),
                                        '<span style="background: #ffeb3b; padding: 2px 4px; border-radius: 3px;">$1</span>'
                                      ) : (t.text || "No text available")
                                    } }
                                  />
                                ) }
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    }) }
                  </List>
                ) : searchQuery ? (
                  <Box textAlign="center" py={ 4 }>
                    <Typography color="#888" fontSize={ 16 } mb={ 1 }>
                      No results found
                    </Typography>
                    <Typography color="#aaa" fontSize={ 14 }>
                      Try searching for different keywords or speaker names
                    </Typography>
                  </Box>
                ) : (
                  <Box textAlign="center" py={ 4 }>
                    <Typography color="#888" fontSize={ 16 } mb={ 1 }>
                      Transcript not available
                    </Typography>
                    <Typography color="#aaa" fontSize={ 14 }>
                      { transcriptLoading ? "Loading transcript..." : "No transcript data found for this meeting" }
                    </Typography>
                  </Box>
                ) }
              </Box>
            ) }
            { transcriptTab === 1 && (
              <Box sx={ { maxHeight: 700, overflowY: "auto", px: 0, pb: 2, pt: 1 } }>
                { currentQaData.length > 0 ? (
                  currentQaData.map((qa, idx) => (
                    <Box
                      key={ idx }
                      mb={ 3 }
                      p={ 2 }
                      sx={ {
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        background: "#fafafa",
                      } }
                    >
                      <Typography
                        fontWeight={ 600 }
                        fontSize={ 14 }
                        color="#396afc"
                        mb={ 1 }
                      >
                        Q: { qa.question || "No question provided" }
                      </Typography>
                      <Typography fontSize={ 14 } color="#555">
                        A: { qa.answer || "No answer provided" }
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="#888" textAlign="center">
                    No Q&A found for this meeting.
                  </Typography>
                ) }
              </Box>
            ) }
          </Box>
        ) }
      </Box>


    </Box>
  );
}
